import React from "react";
import styled from "styled-components";
import { Role } from "features/whop/whopLanding/WhopLandingPage.tsx";
import {
  SectionContainer,
  HeadingArea,
  SectionHeadline,
} from "features/whop/whopLanding/components/shared.tsx";
import { ScrollReveal } from "features/whop/whopLanding/components/animations.tsx";
import { benefitsContent } from "features/whop/whopLanding/content.ts";

interface BenefitsProps {
  role: Role;
}

export const BenefitsSection: React.FC<BenefitsProps> = ({ role }) => {
  const content = benefitsContent[role];
  return (
    <SectionOuter>
      <Section>
        <ScrollReveal>
          <HeadingArea>
            <Headline>{content.headline}</Headline>
          </HeadingArea>
        </ScrollReveal>
        <Grid>
          {content.benefits.map((benefit, index) => (
            <ScrollReveal
              key={index}
              delay={index * 80}
              style={{ height: "100%" }}
            >
              <Card>
                <IconBox>
                  <BenefitIcon icon={benefit.icon} />
                </IconBox>
                <CardTitle>{benefit.title}</CardTitle>
                <CardDesc>{benefit.description}</CardDesc>
              </Card>
            </ScrollReveal>
          ))}
        </Grid>
      </Section>
    </SectionOuter>
  );
};

const BenefitIcon: React.FC<{ icon: string }> = ({ icon }) => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#2563EB"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d={iconPaths[icon] || iconPaths.zap} />
  </svg>
);

const iconPaths: Record<string, string> = {
  zap: "M13 2L3 14h9l-1 8 10-12h-9l1-8z",
  clock: "M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm0 5v5l4 2",
  target:
    "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zm0-7a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 0v4m0-14v4",
  refresh:
    "M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15",
  clock8: "M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm0 5v5l3 3",
  gift: "M20 12v10H4V12M22 7H2v5h20V7zM12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z",
  smile:
    "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zm-4-9s1 2 4 2 4-2 4-2M9 9h.01M15 9h.01",
  trending: "M23 6l-9.5 9.5-5-5L1 18M17 6h6v6",
  message: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z",
  file: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8",
};

const SectionOuter = styled.div`
  background: ${(p) => (p.theme.isDarkMode ? "#0a0a0a" : "#ffffff")};
`;

const Section = styled(SectionContainer)`
  background: transparent;
`;

const Headline = SectionHeadline;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 40px;
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 48px;
  }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: 16px;
  @media (min-width: 640px) {
    gap: 20px;
    padding: 28px;
  }
  height: 100%;
  box-sizing: border-box;
  background: ${(p) => (p.theme.isDarkMode ? "#111827" : "#F8FAFC")};
  border: 1px solid
    ${(p) =>
      p.theme.isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"};
`;

const IconBox = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  @media (min-width: 640px) {
    width: 60px;
    height: 60px;
    border-radius: 12px;
  }
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => (p.theme.isDarkMode ? "#1E3A5F" : "#EFF6FF")};
  flex-shrink: 0;
`;

const CardTitle = styled.h3`
  font-size: clamp(1.125rem, 3vw, 1.5rem);
  font-weight: 600;
  color: ${(p) => (p.theme.isDarkMode ? "#ffffff" : "#0F172A")};
  margin: 0;
  max-width: 240px;
`;

const CardDesc = styled.p`
  font-size: clamp(0.9rem, 2.5vw, 1.125rem);
  line-height: 1.6;
  color: ${(p) => (p.theme.isDarkMode ? "#9CA3AF" : "#4B5563")};
  margin: 0;
`;
